import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export type NotificationType = 'meeting' | 'transcription' | 'system' | 'reminder' | 'success' | 'error' | 'warning';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  urgent?: boolean;
  actionUrl?: string;
  metadata?: Record<string, any>;
  // For toast-style notifications
  duration?: number;
  dismissible?: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
  userId: string | null;

  // For transient toast notifications
  toasts: Notification[];
}

interface NotificationsActions {
  // Initialization
  initialize: (userId: string) => void;
  cleanup: () => void;

  // CRUD operations
  fetchNotifications: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAll: () => void;

  // Toast-specific (transient, not persisted)
  showToast: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => string;
  dismissToast: (id: string) => void;
  clearToasts: () => void;

  // Convenience methods
  showSuccess: (title: string, message?: string) => string;
  showError: (title: string, message?: string) => string;
  showWarning: (title: string, message?: string) => string;
  showInfo: (title: string, message?: string) => string;
}

export type NotificationsStore = NotificationsState & NotificationsActions;

const initialState: NotificationsState = {
  notifications: [],
  isLoading: false,
  error: null,
  userId: null,
  toasts: [],
};

// Generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useNotificationsStore = create<NotificationsStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      initialize: (userId) => {
        // Re-entrant guard: StrictMode remounts and repeated auth events would otherwise
        // refetch and open a duplicate realtime channel on every call.
        if (get().userId === userId && (window as any).__notificationSubscription) {
          return;
        }

        const existing = (window as any).__notificationSubscription;
        if (existing) {
          existing.unsubscribe();
          delete (window as any).__notificationSubscription;
        }

        set({ userId }, false, 'initialize');
        get().fetchNotifications();

        // Set up real-time subscription
        const subscription = supabase
          .channel(`notifications-${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              logger.info('[NotificationsStore] Real-time update:', payload.eventType);
              get().fetchNotifications();
            }
          )
          .subscribe();

        // Store subscription for cleanup (using window to avoid closure issues)
        (window as any).__notificationSubscription = subscription;
      },

      cleanup: () => {
        const subscription = (window as any).__notificationSubscription;
        if (subscription) {
          subscription.unsubscribe();
          delete (window as any).__notificationSubscription;
        }
        set(initialState, false, 'cleanup');
      },

      fetchNotifications: async () => {
        const userId = get().userId;
        if (!userId) return;

        set({ isLoading: true, error: null }, false, 'fetchNotifications/start');

        try {
          const { data, error } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(50);

          if (error) throw error;

          const notifications: Notification[] = data.map((n) => ({
            id: n.id,
            type: n.type as NotificationType,
            title: n.title,
            message: n.message,
            timestamp: new Date(n.created_at),
            read: n.read,
            urgent: n.urgent || false,
            actionUrl: n.action_url || undefined,
            // metadata is Json in the DB, so it may be a scalar or array; only keep
            // it when it is actually an object.
            metadata:
              n.metadata && typeof n.metadata === 'object' && !Array.isArray(n.metadata)
                ? (n.metadata as Record<string, any>)
                : undefined,
          }));

          set({ notifications, isLoading: false }, false, 'fetchNotifications/success');
        } catch (error) {
          logger.error('[NotificationsStore] Fetch error:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch notifications',
            isLoading: false,
          }, false, 'fetchNotifications/error');
        }
      },

      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: generateId(),
          timestamp: new Date(),
          read: notification.read ?? false,
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications],
        }), false, 'addNotification');

        // Also persist to database if we have a user
        const userId = get().userId;
        if (userId) {
          supabase
            .from('notifications')
            .insert({
              user_id: userId,
              type: notification.type,
              title: notification.title,
              message: notification.message,
              read: notification.read ?? false,
              urgent: notification.urgent,
              action_url: notification.actionUrl,
              metadata: notification.metadata,
            })
            .then(({ error }) => {
              if (error) {
                logger.error('[NotificationsStore] Failed to persist notification:', error);
              }
            });
        }
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }), false, 'removeNotification');
      },

      markAsRead: async (id) => {
        const userId = get().userId;

        // Optimistic update
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }), false, 'markAsRead/optimistic');

        if (userId) {
          const { error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', id)
            .eq('user_id', userId);

          if (error) {
            logger.error('[NotificationsStore] Failed to mark as read:', error);
            // Revert optimistic update
            get().fetchNotifications();
          }
        }
      },

      markAllAsRead: async () => {
        const userId = get().userId;

        // Optimistic update
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }), false, 'markAllAsRead/optimistic');

        if (userId) {
          const { error } = await supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', userId)
            .eq('read', false);

          if (error) {
            logger.error('[NotificationsStore] Failed to mark all as read:', error);
            get().fetchNotifications();
          }
        }
      },

      deleteNotification: async (id) => {
        const userId = get().userId;

        // Optimistic update
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }), false, 'deleteNotification/optimistic');

        if (userId) {
          const { error } = await supabase
            .from('notifications')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

          if (error) {
            logger.error('[NotificationsStore] Failed to delete notification:', error);
            get().fetchNotifications();
          }
        }
      },

      clearAll: () => {
        set({ notifications: [] }, false, 'clearAll');
      },

      // Toast methods
      showToast: (notification) => {
        const id = generateId();
        const toast: Notification = {
          ...notification,
          id,
          timestamp: new Date(),
          read: false,
          duration: notification.duration ?? 5000,
          dismissible: notification.dismissible ?? true,
        };

        set((state) => ({
          toasts: [...state.toasts, toast],
        }), false, 'showToast');

        // Auto-dismiss after duration
        if (toast.duration && toast.duration > 0) {
          setTimeout(() => {
            get().dismissToast(id);
          }, toast.duration);
        }

        return id;
      },

      dismissToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }), false, 'dismissToast');
      },

      clearToasts: () => {
        set({ toasts: [] }, false, 'clearToasts');
      },

      // Convenience methods
      showSuccess: (title, message = '') => {
        return get().showToast({
          type: 'success',
          title,
          message,
          duration: 4000,
        });
      },

      showError: (title, message = '') => {
        return get().showToast({
          type: 'error',
          title,
          message,
          duration: 6000,
        });
      },

      showWarning: (title, message = '') => {
        return get().showToast({
          type: 'warning',
          title,
          message,
          duration: 5000,
        });
      },

      showInfo: (title, message = '') => {
        return get().showToast({
          type: 'system',
          title,
          message,
          duration: 4000,
        });
      },
    })),
    { name: 'notifications-store' }
  )
);

// Selector hooks
export const useNotifications = () => useNotificationsStore((state) => state.notifications);
export const useUnreadCount = () => useNotificationsStore((state) =>
  state.notifications.filter((n) => !n.read).length
);
export const useUrgentNotifications = () => useNotificationsStore((state) =>
  state.notifications.filter((n) => n.urgent && !n.read)
);
export const useToasts = () => useNotificationsStore((state) => state.toasts);
export const useNotificationsLoading = () => useNotificationsStore((state) => state.isLoading);
